import React from 'react';
import PropTypes from 'prop-types';

import * as S from './coterm-tile.module.scss';

// Components
import { ProductName } from '../../../common/product/prod-name';
// import { Price } from '../../../common/product/price';
import { Expiry } from '../../../common/product/expiry';
import { TagLine } from '../../../common/product/tag-line';
// import { TileCoTermMiniSub } from './coterm-mini-sub-tile';
import { SingleProductRow } from '../unified-sub-tile/product-single-row';

// Context Functions
import * as ProductSelectors from '../../../../context/selectors/product-selectors';
import * as UserSelectors from '../../../../context/selectors/user-selectors';
import { setCtxNestedObj } from '../../../../context/ctx-actions';
import { getMaxEntitlement,
  getBmTierData
}    from '../../../../context/selectors/bill-selectors';

import { useGlobalCtx } from '../../../../context/ctx-hook';
import { getEStoreCartUrlAddProduct } from '../../../../helpers/route-helper';
import { CancelDowngradeBox } from '../../../modals/change-plans/cancel-downgrade';
// import { getQknPriceWarn }    from '../../../../helpers/skunotices-helpers';

import * as StateConstants from '../../../../helpers/constants/state-constants';
// import { useAutoRenew }       from '../../../../helpers/hooks/use-auto-renew';

// Selector Functions
import { getBillManagerType } from '../../../../context/selectors/bill-selectors';

const getSubsWithPendingDowngrade = (SUBS) => {
  const downgradeSubs = [];
  SUBS.map((sub) => {
    if (sub?.renewalTierUriName) {
      if (sub?.renewalTierUriName !== sub?.tierUriName) {
        downgradeSubs.push(sub);
      }
    }
  });
  return downgradeSubs;
};

export const orderByProductLine = (arr) => {
  const productOrder = [
    'quicken-us',
    'quicken-ca',
    'billmanager',
    'quicken-online-backup',
    'acme',
    'quicken-estate-manager'
  ];

  const orderMap = new Map(productOrder.map((item, index) => [item, index]));

  return arr.slice().sort((a, b) => {
    const aIndex = orderMap.has(a.productLineUriName)
      ? orderMap.get(a.productLineUriName)
      : Infinity;
    const bIndex = orderMap.has(b.productLineUriName)
      ? orderMap.get(b.productLineUriName)
      : Infinity;

    return aIndex - bIndex;
  });
};

const TileCoTerm = ({isModal = false, subs}) => {
  const {globalDispatch, state} = useGlobalCtx();
  // Get all the coterms subs
  const SUBS = subs;

  // Quicken checks becuase we have to inject Free/paid BM into the sub if it exists.
  const QKN_STRING = state.country === 'us' ? 'quicken-us' : 'quicken-ca';
  const QKN_IN_COTERM_GRP = SUBS.find(sub => sub?.productLineUriName === QKN_STRING);
  const BM_TYPE = getBillManagerType(state);
  const QKN_SUB = ProductSelectors.getSubscription(state, QKN_STRING);
  const BM_MAX_ENT = QKN_SUB ? getMaxEntitlement(state) : false;
  const BM_DATA = QKN_SUB ? getBmTierData(BM_MAX_ENT) : false;
  const HAS_BM_PAID = BM_TYPE === 'PAID' ? state.subscriptions?.['billmanager'] : false;

  //QOB Entitlements
  const HAS_QOB_ENT  = ProductSelectors.getQobHasEntitlement(state);
  const HAS_PAID_QOB = !!state.subscriptions?.['quicken-online-backup'];
  const QOB_ENT_DATA = HAS_QOB_ENT ? ProductSelectors.getShittyHardcodedQobData(state) : false;

  const AR_ON_SUBS = SUBS.filter(sub => sub?.autoRenew === true);
  const AR_OFF_SUBS = SUBS.filter(sub => sub?.autoRenew === false && sub?.active === true);

  const SIMP_AR_ON = AR_ON_SUBS.filter(sub => sub.productLineUriName === 'acme' && sub.active === true && sub.autoRenew === true).length > 0;
  const QKN_AR_ON = AR_ON_SUBS.filter(sub => ['quicken-us', 'quicken-ca'].includes(sub.productLineUriName) && sub.active === true && sub.autoRenew === true).length > 0 ;

  // Filter out addons for our 'parent' sub list, probably need a better way to do this
  const ADDON_PRODUCTLINES = ['quicken-online-backup','billmanager'];
  const parentSubs = AR_ON_SUBS.filter(sub => !ADDON_PRODUCTLINES.includes(sub.productLineUriName));
  const ALL_SUBS_AR_OFF = SUBS.map(sub => sub.autoRenew).every(value => value === false);
  const ANY_SUB_ACTIVE = SUBS.filter(sub => sub.active == true).length >= 1;
  const ALL_SUBS_EXPIRED = SUBS.length === SUBS.filter(sub => sub.active == false).length;

  const EXPIRED_SUBS = SUBS.filter(sub => sub?.active === false && sub?.autoRenew === false);
  const ALL_EXPIRED_SKUS = SUBS.map(sub => {
    // Dont show expired QOB tile if they have a QOB entitlement
    if(sub?.productLineUriName === 'quicken-online-backup' && HAS_QOB_ENT) {
      return;
    }
    if(sub.active === false) {
      const buyNowSku = state.skus[sub?.renewalSku] ? sub?.renewalSku : state.defaultSkus?.[state.country]?.[sub?.tierUriName];
      return buyNowSku;
    }
  });
  const SKU_PARAM = ALL_EXPIRED_SKUS.join(',');

  const EXPIRY_SUB_TO_USE = ALL_SUBS_AR_OFF ? parentSubs[0] : AR_ON_SUBS[0];
  const HAS_PAYMENT = UserSelectors.getProductsPaymentMethod(EXPIRY_SUB_TO_USE, state);

  // Check for downgrades on all the subs
  const PENDING_DOWNGRADES = getSubsWithPendingDowngrade(SUBS);
  const HAS_SIMP_DOWNGRADE = PENDING_DOWNGRADES.filter(sub => sub.productLineUriName === 'acme').length > 0;
  const HAS_QKN_DOWNGRADE = PENDING_DOWNGRADES.filter(sub => ['quicken-us', 'quicken-ca'].includes(sub.productLineUriName)).length > 0;



  const manageSubscription = React.useCallback(
    async () => globalDispatch(setCtxNestedObj('modal', {
      type:       StateConstants.MODALS.SUBSCRIPTION,
      ctx:        {product: SUBS.filter(sub => !ADDON_PRODUCTLINES.includes(sub.productLineUriName))},
      isLoading:  true,
      hasError:   false,
      isDisabled: false
    })),
    [globalDispatch, SUBS]
  );

  return (
    <div className={isModal ? S.coTermModalWrapper : S.coTermWrapper} data-testid="SUB_COTERM">
      <div className={`${S.tile} coterm`}>

        {/* 'Grouped' active subscriptions */}
        {(AR_ON_SUBS.length > 0 || ALL_SUBS_EXPIRED) && (
          <div className={S.subTileContain}>
            <div className={S.subTileLeft}>
              {/* {orderByProductLine(AR_ON_SUBS).map((sub, i) => { */}
              {orderByProductLine(AR_ON_SUBS).map((sub, i) => {
                return (
                  <div className={S.grpWrapper} key={i}>
                    <div className={S.productRow} >
                      <img className={S.rowLogo} alt="square product logo" src={ProductSelectors.getProductLogo(sub)}></img>
                      <div className={S.productName}>
                        <ProductName sub={sub} product={sub.productName} className={S.acmeName} nameStyle='inline'/>
                        <TagLine uriName={sub.tierUriName} className={S.tagLine} />
                      </div>
                    </div>

                    {/* Show BM as an 'addon' entitlement */}
                    {['quicken-us', 'quicken-ca'].includes(sub.productLineUriName) && QKN_IN_COTERM_GRP && BM_TYPE ==='FREE' && BM_DATA &&
                      <div className={S.productRow}>
                        <img className={S.rowLogo} alt="square product logo" src={ProductSelectors.getProductLogo(sub)}></img>
                        <div className={S.productName}>
                          <ProductName sub={BM_DATA} product={BM_DATA.productName} className={S.acmeName} nameStyle='inline'/>
                          <TagLine uriName={BM_DATA.tier} className={S.tagLine} />
                        </div>
                      </div>
                    }

                    {/* Show QOB as an 'addon' entitlement */}
                    {/* This is shit code */}
                    {['quicken-us', 'quicken-ca'].includes(sub.productLineUriName) && HAS_QOB_ENT &&
                      <div className={S.productRow}>
                        <img className={S.rowLogo} alt="square product logo" src={ProductSelectors.getProductLogo(QOB_ENT_DATA)}></img>
                        <div className={S.productName}>
                          <ProductName sub={QOB_ENT_DATA} product={'quicken-online-backup'} className={S.acmeName} nameStyle='inline'/>
                          <TagLine uriName={QOB_ENT_DATA.tierUriName} className={S.tagLine} />
                        </div>
                      </div>
                    }
                  </div>
                );
              })}

              {ALL_SUBS_EXPIRED && (
                EXPIRED_SUBS.map((sub, i) => {
                  return (
                    <div className={S.grpWrapper} key={i}>
                      <div className={S.productRow} >
                        <img className={S.rowLogo} alt="square product logo" src={ProductSelectors.getProductLogo(sub)}></img>
                        <div className={S.productName}>
                          <ProductName sub={sub} product={sub.productName} className={S.acmeName} nameStyle='inline'/>
                          <TagLine uriName={sub.tierUriName} className={S.tagLine} />
                        </div>
                      </div>

                      {/* Show BM as an 'addon' entitlement */}
                      {['quicken-us', 'quicken-ca'].includes(sub.productLineUriName) && QKN_IN_COTERM_GRP && BM_TYPE ==='FREE' && BM_DATA &&
                        <div className={S.productRow}>
                          <img className={S.rowLogo} alt="square product logo" src={ProductSelectors.getProductLogo(sub)}></img>
                          <div className={S.productName}>
                            <ProductName sub={BM_DATA} product={BM_DATA.productName} className={S.acmeName} nameStyle='inline'/>
                            <TagLine uriName={BM_DATA.tier} className={S.tagLine} />
                          </div>
                        </div>
                      }

                      {/* Show QOB as an 'addon' entitlement */}
                      {/* This is shit code */}
                      {['quicken-us', 'quicken-ca'].includes(sub.productLineUriName) && HAS_QOB_ENT &&
                        <div className={S.productRow}>
                          <img className={S.rowLogo} alt="square product logo" src={ProductSelectors.getProductLogo(QOB_ENT_DATA)}></img>
                          <div className={S.productName}>
                            <ProductName sub={QOB_ENT_DATA} product={'quicken-online-backup'} className={S.acmeName} nameStyle='inline'/>
                            <TagLine uriName={QOB_ENT_DATA.tierUriName} className={S.tagLine} />
                          </div>
                        </div>
                      }

                    </div>
                  );
                })
              )}

              {HAS_SIMP_DOWNGRADE && SIMP_AR_ON &&
                <CancelDowngradeBox product={'acme'} className={'mainTile'} />
              }

              {HAS_QKN_DOWNGRADE && QKN_AR_ON &&
                <CancelDowngradeBox product={`quicken-${state.country}`} className={'mainTile'} />
              }

              {ALL_SUBS_EXPIRED &&
                <div className={S.productAlert}>
                  <p className={S.messageTitle}>{'Expired Subscription'}</p>
                  <p>{'This subscription is no longer active'}</p>
                </div>
              }
            </div>

            <div className={S.subTileRight}>
              <div className={`${S.actionBtnWrapper}`} >
                {!isModal && ANY_SUB_ACTIVE && HAS_PAYMENT && (
                  <button
                    type="button"
                    data-testid="BTN_COTERM_MANAGE_SUB"
                    onClick={manageSubscription}
                    className={S.ctaBtnInvert}
                  >Manage Subscription</button>
                )}

                {!isModal && !HAS_PAYMENT && (
                  <button
                    type="button"
                    data-testid="BTN_COTERM_MANAGE_SUB"
                    onClick={manageSubscription}
                    className={S.ctaBtn}
                  >Add Billing</button>
                )}

                {ALL_SUBS_EXPIRED &&
                  <a
                    type="button"
                    data-testid="BTN_COTERM_BUY_NOW"
                    href={getEStoreCartUrlAddProduct(SKU_PARAM, state.country)}
                    className={S.ctaBtn}
                  >Buy Now</a>
                }
              </div>

              {ALL_SUBS_EXPIRED ?
                <Expiry sub={EXPIRED_SUBS?.[0]} product={EXPIRED_SUBS?.[0]?.productLineUriName} className={S.productExpiry} />
                : <Expiry sub={EXPIRY_SUB_TO_USE} product={EXPIRY_SUB_TO_USE?.productLineUriName} className={S.productExpiry} />
              }
            </div>
          </div>
        )}


        {/* 'Broken out' subs within the coterm subscription */}
        {AR_OFF_SUBS.map((sub, i) =>
          <SingleProductRow key={i} sub={sub} testId={'COTERM'}/>
        )}


        {BM_TYPE == 'PAID' && QKN_IN_COTERM_GRP &&
          <SingleProductRow sub={HAS_BM_PAID} testId={'BM'}/>
        }

        {!ALL_SUBS_EXPIRED && (
          EXPIRED_SUBS.map((sub, i) =>
            <SingleProductRow key={i} sub={sub} testId={'QKN'}/>
          ))
        }
      </div>
    </div>
  );
};


TileCoTerm.displayName  = 'TileCoTerm';
TileCoTerm.propTypes    = {
  isModal: PropTypes.bool,
  subs:    PropTypes.array
};
TileCoTerm.defaultProps = {
  isModal: false,
  subs: []
};

export { TileCoTerm };